import { SITE_CODE } from "@/config/common";
import { cn } from "@/lib/utils";
import type { Facet } from "@apptus/esales-api";
import { Market } from "@features/market/config/i18n-config";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "@ui/accordion";
import type { ComponentProps } from "react";
import CheckboxFilter from "./CheckboxFilter";
import RangeFilter from "./RangeFilter";
import TextFilter from "./TextFilter";

export default function Filter({
  facets,
  market,
  className,
}: { facets: Facet[]; market: Market } & ComponentProps<"div">) {
  const identifier = `${SITE_CODE}_${market.countryCode}_`;
  const activeFacets = facets
    .filter((facet) => {
      if (
        facet.type === "TEXT" ||
        facet.type === "COLOR" ||
        facet.type === "SIZE"
      ) {
        return facet.selectedCount > 0;
      }
      if (facet.type === "RANGE") {
        return (
          facet.minSelected !== undefined || facet.maxSelected !== undefined
        );
      }
      if (facet.type === "CHECKBOX") {
        return facet.selected;
      }
      return false;
    })
    .map((facet) => facet.id);
  return (
    <Accordion
      type="multiple"
      defaultValue={activeFacets}
      className={cn(className)}
    >
      {facets.map((facet) => {
        if (facet.id === "custom.totalCategoryCodes") return null;
        if (facet.type === "RANGE") {
          return (
            <AccordionItem
              key={facet.id}
              value={facet.id}
              disabled={facet.min === facet.max}
            >
              <AccordionHeader>
                <AccordionTrigger className="gap-x-4 px-4 py-2">
                  {facet.label}
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent className="overflow-hidden px-4 py-2 text-base text-gray-600">
                <RangeFilter
                  key={String(facet.min) + String(facet.max)}
                  facet={facet}
                  market={market}
                />
              </AccordionContent>
            </AccordionItem>
          );
        }
        if (facet.type === "CHECKBOX") {
          return (
            <AccordionItem key={facet.id} value={facet.id}>
              <AccordionHeader>
                <AccordionTrigger className="gap-x-4 px-4 py-2">
                  {facet.label}
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent className="overflow-hidden px-4 py-2 text-base text-gray-600">
                <CheckboxFilter facet={facet} />
              </AccordionContent>
            </AccordionItem>
          );
        }
        if (
          facet.type === "TEXT" ||
          facet.type === "COLOR" ||
          facet.type === "SIZE"
        ) {
          const values =
            facet.type === "SIZE"
              ? facet.sizeTypes.flatMap((sizeType) =>
                  sizeType.formats.flatMap((format) => format.values),
                )
              : facet.values;
          return (
            <AccordionItem key={facet.id + values.length} value={facet.id}>
              <AccordionHeader>
                <AccordionTrigger className="gap-x-4 px-4 py-2">
                  {facet.label}{" "}
                  {facet.selectedCount > 0 && (
                    <span className="font-inline bg-black px-1.5 text-xs font-bold text-white">
                      {facet.selectedCount}
                    </span>
                  )}
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent className="overflow-hidden px-4 py-2 text-base text-gray-600">
                <TextFilter
                  identifier={identifier}
                  facet={{ id: facet.id, values }}
                />
              </AccordionContent>
            </AccordionItem>
          );
        }
        return null;
      })}
    </Accordion>
  );
}
